import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Alert, Button, Card, Input, Popconfirm, Select, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { useUserStore } from "@/stores/react/authStore";

export interface PopUpAdvertRow {
    id: number;
    name?: string;
    displayPage?: number | string;
    terminal?: Array<number | string>;
    state?: number | string;
    createdTime?: string;
}

export interface PopUpAdvertQuery {
    page: number;
    size: number;
    keyword?: string;
    terminal?: number;
    state?: number;
    sortKey?: string;
    sortOrder?: string;
}

export interface PopUpAdvertPageResult {
    list?: PopUpAdvertRow[];
    total?: number;
}

export interface PopUpAdvertPageProps {
    loadAdverts?: (query: PopUpAdvertQuery) => Promise<PopUpAdvertPageResult>;
    remove?: (id: number) => Promise<unknown>;
    updateState?: (id: number, state: number) => Promise<unknown>;
    permissions?: string[];
}

const defaultQuery: PopUpAdvertQuery = { page: 1, size: 10 };
const displayPageLabels: Record<number, string> = {
    1: "首页",
    2: "砂石指数首页",
    3: "运价指数首页",
    4: "资讯首页",
    5: "查船页",
    6: "查车页",
    7: "船运需求列表",
    8: "运单页",
    11: "会员页",
    12: "认证页",
    13: "关于我们"
};
const terminalLabels: Record<number, string> = { 1: "PC端", 2: "链云小程序", 4: "链云App" };
const stateLabels: Record<number, string> = { 1: "草稿", 2: "已启用", 3: "已禁用" };
const terminalOptions = Object.entries(terminalLabels).map(([value, label]) => ({ value: Number(value), label }));
const stateOptions = Object.entries(stateLabels).map(([value, label]) => ({ value: Number(value), label }));

/** 复用 Vue 弹窗广告分页接口。 */
export async function loadPopUpAdverts(query: PopUpAdvertQuery): Promise<PopUpAdvertPageResult> {
    const response = await request.service.get<PopUpAdvertPageResult>("/dataService/admin/pop-up-advert/paging", {
        params: query
    });
    return response.data;
}

/** 复用 Vue 弹窗广告删除接口。 */
export async function deletePopUpAdvert(id: number): Promise<unknown> {
    const response = await request.service.delete(`/dataService/admin/pop-up-advert/${id}`);
    return response.data;
}

/** 复用 Vue 弹窗广告启用/禁用接口。 */
export async function updatePopUpAdvertState(id: number, state: number): Promise<unknown> {
    const response = await request.service.put(`/dataService/admin/pop-up-advert/update/state/${id}/${state}`);
    return response.data;
}

/** 将 Vue 列表筛选与排序字段转换为分页请求参数。 */
export function buildPopUpAdvertQuery(values: Omit<PopUpAdvertQuery, "page" | "size"> = {}): PopUpAdvertQuery {
    const query: PopUpAdvertQuery = { ...defaultQuery };
    if (values.keyword) query.keyword = values.keyword;
    if (values.terminal !== undefined) query.terminal = values.terminal;
    if (values.state !== undefined) query.state = values.state;
    if (values.sortKey && values.sortOrder) {
        query.sortKey = values.sortKey;
        query.sortOrder = values.sortOrder;
    }
    return query;
}

function formatDate(value?: string): string {
    return value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE;
}

function formatEnum(labels: Record<number, string>, value?: number | string): string {
    return labels[Number(value)] || DEFAULT_VALUE;
}

function formatTerminals(values?: Array<number | string>): string {
    if (!values?.length) return DEFAULT_VALUE;
    return values.map(value => terminalLabels[Number(value)] || DEFAULT_VALUE).join(", ");
}

/** 弹窗广告列表，保留 Vue 的筛选字段、状态动作和分页契约。 */
export function PopUpAdvertPage({
    loadAdverts: load = loadPopUpAdverts,
    remove = deletePopUpAdvert,
    updateState = updatePopUpAdvertState,
    permissions
}: PopUpAdvertPageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const storeBanButton = useUserStore(state => state.banButton);
    // 保持 Vue v-auth 语义：显式测试权限优先，否则读取当前登录用户权限。
    const canManage = (permissions ?? storePermissions).includes("ROLE_SS_POPUP_W");
    const banButton = storeBanButton;
    const [rows, setRows] = useState<PopUpAdvertRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<PopUpAdvertQuery>(defaultQuery);
    const [keyword, setKeyword] = useState("");
    const [terminal, setTerminal] = useState<number | undefined>();
    const [state, setState] = useState<number | undefined>();
    const [reloadToken, setReloadToken] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        let disposed = false;
        void load(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("弹窗广告列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [load, query, reloadToken]);

    const submitFilters = () => setQuery(buildPopUpAdvertQuery({ keyword, terminal, state }));
    const runAction = async (action: () => Promise<unknown>, failureMessage: string) => {
        try {
            await action();
            setErrorMessage(null);
            setReloadToken(value => value + 1);
        } catch {
            setErrorMessage(failureMessage);
        }
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 弹窗广告列表的字段、排序和状态操作插槽。
    const columns: TableColumnProps<PopUpAdvertRow>[] = [
        {
            key: "id",
            label: "编号",
            width: 92,
            prop: "id",
            render: ({ row }) => (
                <Button
                    aria-label={`查看弹窗广告详情 ${row.id}`}
                    type="link"
                    onClick={() => void navigate(`/userOperation/popUpAdvert/detail?id=${row.id}`)}
                >
                    {row.id}
                </Button>
            )
        },
        { key: "name", label: "标题", width: 176, prop: "name", render: ({ row }) => row.name || DEFAULT_VALUE },
        {
            key: "displayPage",
            label: "显示页面",
            width: 120,
            prop: "displayPage",
            render: ({ row }) => formatEnum(displayPageLabels, row.displayPage)
        },
        {
            key: "terminal",
            label: "适用终端",
            width: 350,
            prop: "terminal",
            render: ({ row }) => formatTerminals(row.terminal)
        },
        {
            key: "state",
            label: "状态",
            width: 92,
            prop: "state",
            render: ({ row }) => formatEnum(stateLabels, row.state)
        },
        {
            key: "createdTime",
            label: "创建时间",
            width: 156,
            prop: "createdTime",
            sortable: true,
            render: ({ row }) => formatDate(row.createdTime)
        },
        {
            key: "operation",
            className: "compact",
            label: "操作",
            width: 175,
            prop: "id",
            render: ({ row }) => {
                if (!canManage) return null;
                const rowState = Number(row.state);
                if (rowState === 2) {
                    return (
                        <Button
                            aria-label={`禁用弹窗广告 ${row.id}`}
                            disabled={banButton}
                            type="link"
                            onClick={() => void runAction(() => updateState(row.id, 3), "弹窗广告禁用失败")}
                        >
                            禁用
                        </Button>
                    );
                }
                return (
                    <Space>
                        <Button
                            aria-label={`编辑弹窗广告 ${row.id}`}
                            disabled={banButton}
                            type="link"
                            onClick={() => void navigate(`/userOperation/popUpAdvert/edit?id=${row.id}`)}
                        >
                            编辑
                        </Button>
                        <Button
                            aria-label={`启用弹窗广告 ${row.id}`}
                            disabled={banButton}
                            type="link"
                            onClick={() => void runAction(() => updateState(row.id, 2), "弹窗广告启用失败")}
                        >
                            启用
                        </Button>
                        <Popconfirm
                            title="确定删除该弹窗广告吗？"
                            onConfirm={() => void runAction(() => remove(row.id), "弹窗广告删除失败")}
                        >
                            <Button disabled={banButton} aria-label={`删除弹窗广告 ${row.id}`} type="link">
                                删除
                            </Button>
                        </Popconfirm>
                    </Space>
                );
            }
        }
    ];

    return (
        <section aria-label="pop-up-advert-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col">
                <ZhTable<PopUpAdvertRow>
                    data={rows}
                    columns={normalizeOperationColumns(columns, rows)}
                    pagination={false}
                    rowKey={row => String(row.id)}
                    onSortChange={({ order }) => {
                        // Vue 仅对创建时间提交 custom 排序，并在排序后回到第一页。
                        setQuery(current => ({
                            ...current,
                            page: 1,
                            sortKey: order ? "created_time" : undefined,
                            sortOrder: order === "ascending" ? "asc" : order === "descending" ? "desc" : undefined
                        }));
                    }}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Space className="mb-4" wrap>
                                <Typography.Title className="mb-0 mt-0" level={4}>
                                    弹窗广告
                                </Typography.Title>
                            </Space>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Space className="admin-table-filter-form pop-up-advert-filter mb-4" wrap>
                                {canManage ? (
                                    <Button
                                        className="pop-up-advert-create-button"
                                        disabled={banButton}
                                        type="primary"
                                        onClick={() => void navigate("/userOperation/popUpAdvert/add")}
                                    >
                                        新增
                                    </Button>
                                ) : null}
                                <Input
                                    className="pop-up-advert-keyword"
                                    aria-label="弹窗广告关键词"
                                    placeholder="请输入标题关键词或编号搜索"
                                    value={keyword}
                                    onChange={event => setKeyword(event.target.value)}
                                />
                                <Select
                                    className="pop-up-advert-terminal"
                                    allowClear
                                    aria-label="适用终端"
                                    placeholder="请选择适用终端"
                                    value={terminal}
                                    options={terminalOptions}
                                    onChange={value => setTerminal(value)}
                                />
                                <Select
                                    className="pop-up-advert-state"
                                    allowClear
                                    aria-label="弹窗广告状态"
                                    placeholder="请选择状态"
                                    value={state}
                                    options={stateOptions}
                                    onChange={value => setState(value)}
                                />
                                <Button
                                    aria-label="查询弹窗广告"
                                    className="pop-up-advert-search"
                                    type="primary"
                                    onClick={submitFilters}
                                >
                                    搜索
                                </Button>
                            </Space>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                hideOnSinglePage
                                onChange={(page, size) => setQuery(current => ({ ...current, page, size }))}
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}
